import React, { useState } from 'react';

export default function PlayerChoices({ 
  players, 
  currentUserId, 
  tradeAgreements = {}, 
  onRequestTradeAgreement, 
  onCancelTradeAgreement,
  stockHoldings = {},
  onBuyStock,
  onBuybackStock,
  isStockTradingEnabled = false,
  tileOwners,
  worldData,
  onPropertyClick,
  onExecuteAction,
  activeDebuffs = {},
  containerStyle = {}
}) {
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [sortBy, setSortBy] = useState('value');
  const [stockCooldowns, setStockCooldowns] = useState({});
  const [buybackCooldowns, setBuybackCooldowns] = useState({});
  const [tradeRequestCooldowns, setTradeRequestCooldowns] = useState({});
  const [selectedAction, setSelectedAction] = useState('criticise');
  const [targetPlayer, setTargetPlayer] = useState('');
  const [actionSuccessMessage, setActionSuccessMessage] = useState('');
  const [actionErrorMessage, setActionErrorMessage] = useState('');
  
  const availableActions = {
    criticise: { name: 'Criticise', cost: 100000, effect: '-20% Income' },
    fakeNews: { name: 'Spread Fake News', cost: 250000, effect: '-50% Income' },
    sabotage: { name: 'Sabotage', cost: 500000, effect: '-90% Income' }
  };
  
  // NEW: Add state to track previous valid player data
  const [previousPlayers, setPreviousPlayers] = useState(players);
  
  // NEW: Filter out invalid player updates (all zeros)
  const validPlayers = React.useMemo(() => {
    // Check if all players have zero or invalid values
    const allZeros = players.every(p => (p.value || 0) === 0 || (p.value || 0) === 1);
    
    if (allZeros && previousPlayers.length > 0) {
      // Return previous valid data if current data is all zeros
      console.warn('Received invalid player data (all zeros), using previous valid data');
      return previousPlayers;
    }
    
    // Update previous valid data
    if (!allZeros && players.length > 0) {
      setPreviousPlayers(players);
    }
    
    return players;
  }, [players, previousPlayers]);

  const getTradeAgreementStatus = (playerId) => {
    const agreements = Object.values(tradeAgreements);
    const activeAgreement = agreements.find(ta => 
      (ta.requesterId === currentUserId || ta.targetId === currentUserId) &&
      (ta.requesterId === playerId || ta.targetId === playerId) &&
      ta.status === 1
    );
    if (activeAgreement) return 'active';

    const pendingOut = agreements.find(ta => 
      ta.requesterId === currentUserId && ta.targetId === playerId && ta.status === 0
    );
    if (pendingOut) return 'pending_out';

    const pendingIn = agreements.find(ta => 
      ta.requesterId === playerId && ta.targetId === currentUserId && ta.status === 0
    );
    if (pendingIn) return 'pending_in';

    const hasActiveAgreement = agreements.some(ta => 
      (ta.requesterId === currentUserId || ta.targetId === currentUserId || 
       ta.requesterId === playerId || ta.targetId === playerId) && 
      ta.status === 1
    );
    if (hasActiveAgreement) return 'blocked';

    return 'none';
  };

  const handleTradeButtonClick = (playerId) => {
    const status = getTradeAgreementStatus(playerId);
    if (status === 'active') {
      onCancelTradeAgreement();
    } else if (status === 'none') {
      // Check cooldown
      if (tradeRequestCooldowns[currentUserId]) return;
      
      onRequestTradeAgreement(playerId);
      
      // Set cooldown
      setTradeRequestCooldowns(prev => ({ ...prev, [currentUserId]: 15 }));
      const interval = setInterval(() => {
        setTradeRequestCooldowns(prev => {
          const remaining = prev[currentUserId] - 1;
          if (remaining <= 0) {
            clearInterval(interval);
            const { [currentUserId]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [currentUserId]: remaining };
        });
      }, 1000);
    }
  };

  const getTradeButtonText = (status, isOnCooldown, cooldownRemaining) => {
    if (isOnCooldown) return cooldownRemaining;
    switch (status) {
      case 'active': return '−';
      case 'pending_out': return '⏳';
      case 'pending_in': return '📨';
      case 'blocked': return '🚫';
      default: return '+';
    }
  };

  const getTradeButtonColor = (status) => {
    switch (status) {
      case 'active': return '#FF6B6B';
      case 'pending_out': return '#FFB347';
      case 'pending_in': return '#87CEEB';
      case 'blocked': return '#808080';
      default: return '#4CAF50';
    }
  };

  const getTradeButtonTitle = (status, playerName, isOnCooldown, cooldownRemaining) => {
    if (isOnCooldown) return `Cooldown: ${cooldownRemaining}s`;
    switch (status) {
      case 'active': return `Cancel trade agreement with ${playerName}`;
      case 'pending_out': return `Trade request sent to ${playerName}`;
      case 'pending_in': return `${playerName} sent you a trade request`;
      case 'blocked': return 'Trade agreements unavailable';
      default: return `Send trade request to ${playerName}`;
    }
  };

  const getStockCount = (acquirerId, targetId) => {
    if (!stockHoldings[acquirerId]) return 0;
    const holding = stockHoldings[acquirerId].find(h => h.targetPlayerId === targetId);
    return holding ? holding.stockCount : 0;
  };

  const getStockHolders = (targetId) => {
    const holders = [];
    Object.entries(stockHoldings).forEach(([acquirerId, holdings]) => {
      const holding = holdings.find(h => h.targetPlayerId === targetId);
      if (holding && holding.stockCount > 0) {
        holders.push({ acquirerId, stockCount: holding.stockCount });
      }
    });
    return holders;
  };

  const calculateStockPrice = (targetPlayer) => {
    return Math.floor(targetPlayer.value * 0.2);
  };

  const sortedPlayers = [...validPlayers] // Use validPlayers instead of players
    .sort((a, b) => {
      if (a.isEliminated && !b.isEliminated) return 1;
      if (!a.isEliminated && b.isEliminated) return -1;
      if (sortBy === 'value') {
        return (b.value || 0) - (a.value || 0);
      } else if (sortBy === 'profit') {
        return (b.profitPerMinute || 0) - (a.profitPerMinute || 0);
      } else {
        return b.budget - a.budget;
      }
    });

  const handleBuyStock = (targetId, playerName, stocksOwned) => {
    if (stockCooldowns[currentUserId]) return;

    const executeBuy = () => {
      onBuyStock(targetId);
      setStockCooldowns(prev => ({ ...prev, [currentUserId]: 5 }));
      const interval = setInterval(() => {
        setStockCooldowns(prev => {
          const remaining = prev[currentUserId] - 1;
          if (remaining <= 0) {
            clearInterval(interval);
            const { [currentUserId]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [currentUserId]: remaining };
        });
      }, 1000);
    };

    if (stocksOwned >= 4) {
      if (window.confirm(`This will be your 5th stock of ${playerName}. You will take over all their assets. Continue?`)) {
        executeBuy();
      }
    } else {
      executeBuy();
    }
  };

  const handleBuybackStock = (acquirerId, stockPrice) => {
    if (buybackCooldowns[currentUserId]) return;

    const holderPlayer = players.find(p => p.userId === acquirerId);
    const holderName = holderPlayer?.displayName || 'Unknown';
    
    if (window.confirm(`Buy back 1 stock from ${holderName} for $${stockPrice.toLocaleString()}?`)) {
      onBuybackStock(acquirerId);
      setBuybackCooldowns(prev => ({ ...prev, [currentUserId]: 15 }));
      const interval = setInterval(() => {
        setBuybackCooldowns(prev => {
          const remaining = prev[currentUserId] - 1;
          if (remaining <= 0) {
            clearInterval(interval);
            const { [currentUserId]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [currentUserId]: remaining };
        });
      }, 1000);
    }
  };

  const currentPlayer = validPlayers.find(p => p.userId === currentUserId); // Use validPlayers
  const isCurrentUserEliminated = currentPlayer?.isEliminated || false;

  const getTileName = (tileType) => {
    const names = {
      3: 'Farm', 4: 'Market', 5: 'Mine', 6: 'Fishery', 7: 'Port',
      8: 'Oil Rig', 9: 'Oil Well', 10: 'Lumber Mill', 11: 'Power Plant',
      12: 'Factory', 13: 'Bank', 14: 'Construction Company'
    };
    return names[tileType] || 'Unknown';
  };

  const getTileIcon = (tileType) => {
    const icons = {
      3: '🌾', 4: '🏪', 5: '⛏️', 6: '🐟', 7: '⚓', 8: '🛢️', 9: '🕳️',
      10: '🏗️', 11: '⚡', 12: '🏭', 13: '🏦', 14: '🏢'
    };
    return icons[tileType] || '📍';
  };

  const myProperties = React.useMemo(() => {
    if (!tileOwners || !worldData) return [];
    
    return Object.entries(tileOwners)
      .filter(([_, ownership]) => ownership.owner === currentUserId)
      .map(([key, ownership]) => {
        const [x, y] = key.split(',').map(Number);
        const tileIndex = y * worldData.width + x;
        const tileType = worldData.tiles[tileIndex];
        return {
          key,
          x,
          y,
          type: tileType,
          purchasePrice: ownership.purchasePrice
        };
      })
      .sort((a, b) => a.type - b.type); // Sort by type
  }, [tileOwners, worldData, currentUserId]);

  return (
    <div style={{
      position: 'absolute',
      top: '10px',
      left: '10px',
      background: 'rgba(0,0,0,0.8)',
      color: 'white',
      padding: '15px',
      borderRadius: '8px',
      fontFamily: 'Arial, sans-serif',
      width: '290px',
      border: '2px solid #FFD700',
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 20px)', // Enforce max height on non-mobile so it scrolls too
      ...containerStyle
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          style={{
            background: 'transparent',
            color: '#FFD700',
            border: 'none',
            fontSize: '1.1em',
            fontWeight: 'bold',
            textAlign: 'center',
            cursor: 'pointer',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
            outline: 'none',
            paddingRight: '20px',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right center',
            backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFD700%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
            backgroundSize: '12px'
          }}
        >
          <option value="leaderboard" style={{ background: '#333', color: '#fff' }}>🏆 Leaderboard</option>
          <option value="properties" style={{ background: '#333', color: '#fff' }}>🏠 My Properties ({myProperties.length})</option>
          <option value="actions" style={{ background: '#333', color: '#fff' }}>⚔️ Actions</option>
          <option value="trade" style={{ background: '#333', color: '#fff' }}>🤝 Trade</option>
          <option value="stocks" style={{ background: '#333', color: '#fff' }}>📈 Stocks</option>
        </select>
      </div>
      
      {activeTab === 'leaderboard' ? (
        <>
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '10px',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => setSortBy('value')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: sortBy === 'value' ? '#FFD700' : 'rgba(255, 255, 255, 0.2)',
                color: sortBy === 'value' ? '#000' : '#FFF',
                cursor: 'pointer',
                fontSize: '0.75em',
                fontWeight: 'bold'
              }}
            >
              By Value
            </button>
            <button
              onClick={() => setSortBy('budget')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: sortBy === 'budget' ? '#FFD700' : 'rgba(255, 255, 255, 0.2)',
                color: sortBy === 'budget' ? '#000' : '#FFF',
                cursor: 'pointer',
                fontSize: '0.75em',
                fontWeight: 'bold'
              }}
            >
              By Budget
            </button>
            <button
              onClick={() => setSortBy('profit')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: sortBy === 'profit' ? '#FFD700' : 'rgba(255, 255, 255, 0.2)',
                color: sortBy === 'profit' ? '#000' : '#FFF',
                cursor: 'pointer',
                fontSize: '0.75em',
                fontWeight: 'bold'
              }}
            >
              By Profit
            </button>
          </div>
          
          <div style={{ 
            fontSize: '0.85em',
            height: '190px',
            overflowY: 'auto'
          }}>
            {sortedPlayers.map((player, index) => {
          const tradeStatus = player.userId !== currentUserId ? getTradeAgreementStatus(player.userId) : 'self';
          const stocksOwned = getStockCount(currentUserId, player.userId);
          const stockHolders = getStockHolders(player.userId);
          const stockPrice = calculateStockPrice(player);
          const isEliminated = player.isEliminated || false;
          const cooldownRemaining = stockCooldowns[currentUserId] || 0;
          const isOnCooldown = cooldownRemaining > 0;
          const buybackCooldownRemaining = buybackCooldowns[currentUserId] || 0;
          const isOnBuybackCooldown = buybackCooldownRemaining > 0;
          const tradeRequestCooldownRemaining = tradeRequestCooldowns[currentUserId] || 0;
          const isOnTradeRequestCooldown = tradeRequestCooldownRemaining > 0;

          return (
            <div key={player.userId} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 8px',
              marginBottom: '4px',
              backgroundColor: isEliminated 
                ? 'rgba(128, 128, 128, 0.2)' 
                : player.userId === currentUserId 
                  ? 'rgba(255, 215, 0, 0.2)' 
                  : 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              border: isEliminated
                ? '1px solid rgba(128, 128, 128, 0.4)'
                : player.userId === currentUserId 
                  ? '1px solid #FFD700' 
                  : '1px solid rgba(255, 255, 255, 0.2)',
              opacity: isEliminated ? 0.6 : 1
            }}>
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <span style={{ 
                  minWidth: '20px', 
                  fontWeight: 'bold',
                  color: isEliminated 
                    ? '#808080'
                    : index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#FFF'
                }}>
                  {isEliminated ? '💀' : `${index + 1}.`}
                </span>
                <div style={{ 
                  marginLeft: '8px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: player.userId === currentUserId ? 'column' : 'row',
                  alignItems: player.userId === currentUserId ? 'flex-start' : 'center',
                  justifyContent: player.userId === currentUserId ? 'flex-start' : 'space-between'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ 
                      color: isEliminated ? '#808080' : player.color, 
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                      textDecoration: isEliminated ? 'line-through' : 'none'
                    }}>
                      {player.displayName}
                      {player.userId === currentUserId && ' (You)'}
                      {isEliminated && ' [ELIMINATED]'}
                    </span>
                    {!isEliminated && stocksOwned > 0 && (
                      <span style={{ fontSize: '0.7em', color: '#90EE90' }}>
                        📊 {stocksOwned} stock{stocksOwned > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span style={{ 
                fontWeight: 'bold',
                color: isEliminated ? '#808080' : (sortBy === 'profit' ? (player.profitPerMinute >= 0 ? '#90EE90' : '#FF6B6B') : '#90EE90'),
                marginLeft: '5px'
              }}>
                {sortBy === 'value' 
                  ? `$${(player.value || 0).toLocaleString()}` 
                  : sortBy === 'profit'
                    ? `${player.profitPerMinute >= 0 ? '+' : ''}$${(player.profitPerMinute || 0).toLocaleString()}/min`
                    : `$${player.budget.toLocaleString()}`}
              </span>
            </div>
          );
        })}
          </div>
        </>
      ) : activeTab === 'properties' ? (
        <div style={{ 
          height: '228px', // Matches the total height of the leaderboard content (190px + 38px for buttons)
          overflowY: 'auto'
        }}>
          {myProperties.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#aaa', marginTop: '20px', fontStyle: 'italic' }}>
              You don't own any properties yet.
            </div>
          ) : (
            myProperties.map(prop => (
              <div 
                key={prop.key}
                onClick={() => onPropertyClick(prop.x, prop.y)}
                style={{
                  padding: '8px',
                  marginBottom: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = '#FFD700';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px', fontSize: '1.2em' }}>{getTileIcon(prop.type)}</span>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '0.9em' }}>{getTileName(prop.type)}</div>
                    <div style={{ color: '#aaa', fontSize: '0.75em' }}>
                      Coords: {prop.x}, {prop.y}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '1.2em', color: '#FFD700' }}>
                  ⌖
                </div>
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'actions' ? (
        <div style={{
          height: '228px',
          overflowY: 'auto'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.95em', color: '#ccc' }}>Select Action:</label>
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              disabled={isCurrentUserEliminated}
              style={{ width: '100%', padding: '6px', background: '#333', color: 'white', border: '1px solid #555', borderRadius: '4px', fontSize: '0.95em' }}
            >
              <option value="criticise">Criticise (-20% Income, $100k)</option>
              <option value="fakeNews">Spread Fake News (-50% Income, $250k)</option>
              <option value="sabotage">Sabotage (-90% Income, $500k)</option>
            </select>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.95em', color: '#ccc' }}>Select Target:</label>
            <select
              value={targetPlayer}
              onChange={(e) => setTargetPlayer(e.target.value)}
              disabled={isCurrentUserEliminated}
              style={{ width: '100%', padding: '6px', background: '#333', color: 'white', border: '1px solid #555', borderRadius: '4px', fontSize: '0.95em' }}
            >
              <option value="">-- Choose a player --</option>
              {validPlayers.filter(p => !p.isEliminated && p.userId !== currentUserId).map(p => {
                const hasMyAttack = activeDebuffs[p.userId]?.some(d => d.attackerId === currentUserId);
                return (
                  <option key={p.userId} value={p.userId} disabled={hasMyAttack}>
                    {p.displayName} {hasMyAttack ? '(Already Attacked)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          
          <button
            onClick={async () => {
              if (targetPlayer && !isCurrentUserEliminated) {
                if (onExecuteAction) {
                  await onExecuteAction(selectedAction, targetPlayer);
                  const tgtName = validPlayers.find(p => p.userId === targetPlayer)?.displayName || 'Target';
                  setActionSuccessMessage(`Successfully attacked ${tgtName} with ${availableActions[selectedAction].name}!`);
                  setTimeout(() => setActionSuccessMessage(''), 3000);
                  setTargetPlayer('');
                }
              }
            }}
            disabled={isCurrentUserEliminated || !targetPlayer || currentPlayer?.budget < availableActions[selectedAction].cost || activeDebuffs[targetPlayer]?.some(d => d.attackerId === currentUserId)}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '1em',
              marginTop: '5px',
              background: (isCurrentUserEliminated || !targetPlayer || currentPlayer?.budget < availableActions[selectedAction].cost || activeDebuffs[targetPlayer]?.some(d => d.attackerId === currentUserId)) ? '#555' : '#FFD700',
              color: 'black',
              border: 'none',
              borderRadius: '5px',
              fontWeight: 'bold',
              cursor: (isCurrentUserEliminated || !targetPlayer || currentPlayer?.budget < availableActions[selectedAction].cost || activeDebuffs[targetPlayer]?.some(d => d.attackerId === currentUserId)) ? 'not-allowed' : 'pointer',
              opacity: (isCurrentUserEliminated || !targetPlayer || currentPlayer?.budget < availableActions[selectedAction].cost || activeDebuffs[targetPlayer]?.some(d => d.attackerId === currentUserId)) ? 0.6 : 1
            }}
          >
            Execute Action
          </button>
          
          {actionErrorMessage && (
            <div style={{ color: '#FF6B6B', fontSize: '0.9em', marginTop: '6px', textAlign: 'center', fontWeight: 'bold' }}>
              ✕ {actionErrorMessage}
            </div>
          )}

          {actionSuccessMessage && (
            <div style={{ color: '#4CAF50', fontSize: '0.9em', marginTop: '6px', textAlign: 'center', fontWeight: 'bold' }}>
              ✓ {actionSuccessMessage}
            </div>
          )}

          {targetPlayer && activeDebuffs[targetPlayer]?.some(d => d.attackerId === currentUserId) && (
            <div style={{ color: '#FF6B6B', fontSize: '0.85em', marginTop: '6px', textAlign: 'center' }}>
              You already have an active attack against this player. Wait for it to expire.
            </div>
          )}

          {currentPlayer?.budget < availableActions[selectedAction].cost && (
            <div style={{ color: '#FF6B6B', fontSize: '0.85em', marginTop: '6px', textAlign: 'center' }}>
              Not enough funds (${availableActions[selectedAction].cost.toLocaleString()} needed)
            </div>
          )}
        </div>
      ) : activeTab === 'trade' ? (
        <div style={{ height: '228px', overflowY: 'auto' }}>
          <div style={{ padding: '0 5px' }}>
            <h4 style={{ margin: '5px 0', color: '#FFD700', fontSize: '0.9em' }}>My Trade Agreements</h4>
            {sortedPlayers.filter(p => p.userId !== currentUserId && !p.isEliminated).map(player => {
              const tradeStatus = getTradeAgreementStatus(player.userId);
              const isOnTradeRequestCooldown = (tradeRequestCooldowns[currentUserId] || 0) > 0;
              const tradeRequestCooldownRemaining = tradeRequestCooldowns[currentUserId] || 0;

              return (
                <div key={player.userId} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 8px', marginBottom: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{ flex: 1, color: player.color, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {player.displayName}
                  </div>
                  <button
                    onClick={() => handleTradeButtonClick(player.userId)}
                    disabled={isCurrentUserEliminated || tradeStatus === 'pending_out' || tradeStatus === 'pending_in' || tradeStatus === 'blocked' || isOnTradeRequestCooldown}
                    title={isCurrentUserEliminated ? 'You are eliminated' : getTradeButtonTitle(tradeStatus, player.displayName, isOnTradeRequestCooldown, tradeRequestCooldownRemaining)}
                    style={{
                      width: '24px', height: '24px', borderRadius: '4px', border: 'none',
                      backgroundColor: isOnTradeRequestCooldown ? '#808080' : getTradeButtonColor(tradeStatus),
                      color: 'white', cursor: (isCurrentUserEliminated || tradeStatus === 'pending_out' || tradeStatus === 'pending_in' || tradeStatus === 'blocked' || isOnTradeRequestCooldown) ? 'not-allowed' : 'pointer',
                      fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: (isCurrentUserEliminated || tradeStatus === 'blocked' || isOnTradeRequestCooldown) ? 0.5 : 1
                    }}
                  >
                    {getTradeButtonText(tradeStatus, isOnTradeRequestCooldown, tradeRequestCooldownRemaining)}
                  </button>
                </div>
              );
            })}

            <h4 style={{ margin: '15px 0 5px', color: '#FFD700', fontSize: '0.9em' }}>Global Trade Agreements</h4>
            {(() => {
              const activeGlobal = Object.values(tradeAgreements).filter(ta => ta.status === 1 && ta.requesterId !== currentUserId && ta.targetId !== currentUserId);
              if (activeGlobal.length === 0) return <div style={{ fontSize: '0.8em', color: '#aaa', fontStyle: 'italic', marginBottom: '10px' }}>No active agreements between others.</div>;
              
              return activeGlobal.map(ta => {
                const reqPlayer = validPlayers.find(p => p.userId === ta.requesterId);
                const tgtPlayer = validPlayers.find(p => p.userId === ta.targetId);
                if (!reqPlayer || !tgtPlayer) return null;
                return (
                  <div key={ta.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 8px', marginBottom: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <span style={{ color: reqPlayer.color, fontWeight: 'bold' }}>{reqPlayer.displayName}</span>
                    <span style={{ margin: '0 8px', color: '#aaa', fontSize: '0.9em' }}>🤝</span>
                    <span style={{ color: tgtPlayer.color, fontWeight: 'bold' }}>{tgtPlayer.displayName}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : activeTab === 'stocks' ? (
        <div style={{ height: '228px', overflowY: 'auto' }}>
          <div style={{ padding: '0 5px' }}>
            
            <h4 style={{ margin: '5px 0', color: '#FFD700', fontSize: '0.9em' }}>My Stocks</h4>
            {(() => {
              const myHoldings = stockHoldings[currentUserId] || [];
              if (myHoldings.length === 0) return <div style={{ fontSize: '0.8em', color: '#aaa', fontStyle: 'italic', marginBottom: '10px' }}>No stocks owned.</div>;
              return myHoldings.map(h => {
                const targetPlayer = validPlayers.find(p => p.userId === h.targetPlayerId);
                if (!targetPlayer || targetPlayer.isEliminated || h.stockCount === 0) return null;
                return (
                  <div key={h.targetPlayerId} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 8px', marginBottom: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ color: targetPlayer.color, fontWeight: 'bold' }}>{targetPlayer.displayName}</div>
                    <div style={{ color: '#90EE90', fontWeight: 'bold', fontSize: '0.9em' }}>📊 {h.stockCount} stock{h.stockCount > 1 ? 's' : ''}</div>
                  </div>
                );
              });
            })()}

            <h4 style={{ margin: '10px 0 5px', color: '#FFD700', fontSize: '0.9em' }}>Buy Stocks</h4>
            {!isStockTradingEnabled && <div style={{ fontSize: '0.8em', color: '#aaa', fontStyle: 'italic', marginBottom: '10px' }}>Stock trading not yet available.</div>}
            {isStockTradingEnabled && sortedPlayers.filter(p => p.userId !== currentUserId && !p.isEliminated).map(player => {
              const stocksOwned = getStockCount(currentUserId, player.userId);
              const stockPrice = calculateStockPrice(player);
              const isOnCooldown = (stockCooldowns[currentUserId] || 0) > 0;
              const cooldownRemaining = stockCooldowns[currentUserId] || 0;

              return (
                <div key={player.userId} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 8px', marginBottom: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{ flex: 1, color: player.color, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {player.displayName} <span style={{ color: '#aaa', fontSize: '0.8em' }}>(${stockPrice.toLocaleString()})</span>
                  </div>
                  <button
                    onClick={() => handleBuyStock(player.userId, player.displayName, stocksOwned)}
                    disabled={isCurrentUserEliminated || isOnCooldown || currentPlayer?.budget < stockPrice}
                    title={isCurrentUserEliminated ? 'You are eliminated' : (isOnCooldown ? `Cooldown: ${cooldownRemaining}s` : `Buy stock - $${stockPrice.toLocaleString()}`)}
                    style={{
                      width: '24px', height: '24px', borderRadius: '4px', border: 'none',
                      backgroundColor: isOnCooldown ? '#808080' : (stocksOwned >= 4 ? '#FF4444' : '#2196F3'),
                      color: 'white', cursor: (isCurrentUserEliminated || isOnCooldown) ? 'not-allowed' : 'pointer',
                      fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: (isCurrentUserEliminated || isOnCooldown) ? 0.5 : 1
                    }}
                  >
                    {isOnCooldown ? cooldownRemaining : '📈'}
                  </button>
                </div>
              );
            })}

            <h4 style={{ margin: '10px 0 5px', color: '#FFD700', fontSize: '0.9em' }}>Buy Back Options</h4>
            {(() => {
              const stockHolders = getStockHolders(currentUserId);
              if (stockHolders.length === 0) return <div style={{ fontSize: '0.8em', color: '#aaa', fontStyle: 'italic', marginBottom: '10px' }}>No one owns your stock.</div>;
              const stockPrice = calculateStockPrice(currentPlayer || {});
              const isOnBuybackCooldown = (buybackCooldowns[currentUserId] || 0) > 0;
              const buybackCooldownRemaining = buybackCooldowns[currentUserId] || 0;

              return stockHolders.map(holder => {
                const holderPlayer = validPlayers.find(p => p.userId === holder.acquirerId);
                if (!holderPlayer) return null;
                return (
                  <div key={holder.acquirerId} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 8px', marginBottom: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ flex: 1, color: holderPlayer.color, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {holderPlayer.displayName} <span style={{ color: '#aaa', fontSize: '0.8em' }}>({holder.stockCount})</span>
                    </div>
                    <button
                      onClick={() => handleBuybackStock(holder.acquirerId, stockPrice)}
                      disabled={isCurrentUserEliminated || isOnBuybackCooldown || currentPlayer?.budget < stockPrice}
                      title={isCurrentUserEliminated ? 'You are eliminated' : (isOnBuybackCooldown ? `Cooldown: ${buybackCooldownRemaining}s` : `Buy back - $${stockPrice.toLocaleString()}`)}
                      style={{
                        width: '24px', height: '24px', borderRadius: '4px', border: 'none',
                        backgroundColor: isOnBuybackCooldown ? '#808080' : '#FF9800',
                        color: 'white', cursor: (isCurrentUserEliminated || isOnBuybackCooldown) ? 'not-allowed' : 'pointer',
                        fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: (isCurrentUserEliminated || isOnBuybackCooldown) ? 0.5 : 1
                      }}
                    >
                      {isOnBuybackCooldown ? buybackCooldownRemaining : '↩'}
                    </button>
                  </div>
                );
              });
            })()}

          </div>
        </div>
      ) : null}
    </div>
  );
}